import React, { useState, useCallback, useRef } from 'react';
import ReactFlow, { 
  addEdge, 
  Background, 
  Controls, 
  MiniMap,
  useNodesState,
  useEdgesState
} from 'reactflow';
import 'reactflow/dist/style.css';
import { saveAs } from 'file-saver';
import { db } from './firebase';
import { collection, addDoc } from './firestore-compat';

// require() em vez de import — força a resolução pela condição "require" do
// pacote (dist/index.cjs), não pela "import" (dist/index.mjs). O build ESM do
// docx tem um padrão (super() dentro de arrow function com rest params) que o
// Babel desse projeto se recusa a compilar sem @babel/plugin-transform-classes,
// quebrando o build de produção. O CJS tem o mesmo conteúdo já transpilado.
const { Document, Packer, Paragraph, TextRun, ImageRun, AlignmentType } = require('docx');

const initialNodes = [
  { id: '1', position: { x: 0, y: 0 }, data: { label: 'Início' }, type: 'input' },
];

const DOCUMENTOS_DESATIVADO = true;

export default function DocumentManager() {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [docTitle, setDocTitle] = useState('Novo Fluxograma');
  const [docContent, setDocContent] = useState('Descreva o processo aqui...');
  const reactFlowWrapper = useRef(null);

  const onConnect = useCallback((params) => setEdges((eds) => addEdge(params, eds)), [setEdges]);

  const addNode = () => {
    const id = (nodes.length + 1).toString();
    const newNode = {
      id,
      position: { x: Math.random() * 400, y: Math.random() * 400 },
      data: { label: `Passo ${id}` },
    };
    setNodes((nds) => nds.concat(newNode));
  };

  const exportToWord = async () => {
    // Note: To export the actual flowchart as an image, we would need to capture the ReactFlow canvas (e.g. using html-to-image)
    // For this foundational version, we'll create the document structure.
    
    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: docTitle,
                bold: true,
                size: 32,
              }),
            ],
            alignment: AlignmentType.CENTER,
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: "\n" + docContent,
                size: 24,
              }),
            ],
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: "\n[Fluxograma Gerado Integrado Aqui]",
                italics: true,
              }),
            ],
          }),
        ],
      }],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${docTitle}.docx`);
  };

  const s = {
    container: { display: 'flex', height: 'calc(100vh - 60px)', background: '#f8fafc', fontFamily: "'Inter', sans-serif" },
    editor: { width: '350px', background: '#fff', borderRight: '1px solid #e2e8f0', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px', overflowY: 'auto' },
    canvas: { flex: 1, position: 'relative', background: '#f1f5f9' },
    input: { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.9rem', outline: 'none' },
    textarea: { width: '100%', height: '150px', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.9rem', outline: 'none', resize: 'none' },
    btn: { padding: '12px', borderRadius: '8px', border: 'none', background: '#00897b', color: '#fff', fontWeight: 700, cursor: 'pointer' },
    btnSecondary: { padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontWeight: 700, cursor: 'pointer' },
    toolbar: { position: 'absolute', top: '20px', left: '20px', zIndex: 10, display: 'flex', gap: '10px' }
  };

  if (DOCUMENTOS_DESATIVADO) {
    return (
      <div style={{ padding: '80px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: 54, marginBottom: 16 }}>🛠️</div>
        <h3 style={{ color: '#1e293b', marginBottom: 8, fontWeight: 700, fontSize: '1.2rem' }}>Documentos / Fluxos desativado</h3>
        <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Este módulo não está mais em uso.</p>
      </div>
    );
  }

  return (
    <div style={s.container}>
      <div style={s.editor}>
        <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0f172a' }}>📄 Editor de Documentos</h2>
        
        <div>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.85rem' }}>Título do Documento</label>
          <input style={s.input} value={docTitle} onChange={e => setDocTitle(e.target.value)} />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.85rem' }}>Conteúdo / Modelo</label>
          <textarea style={s.textarea} value={docContent} onChange={e => setDocContent(e.target.value)} />
        </div>

        <button style={s.btn} onClick={exportToWord}>📥 Exportar Word (.docx)</button>
        <button style={s.btnSecondary} onClick={() => alert("Salvando modelo no Firebase...")}>💾 Salvar Modelo</button>
      </div>

      <div style={s.canvas} ref={reactFlowWrapper}>
        <div style={s.toolbar}>
          <button style={{...s.btn, background: '#4f46e5'}} onClick={addNode}>+ Adicionar Passo</button>
        </div>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          fitView
        >
          <Background color="#cbd5e1" gap={20} />
          <Controls />
          <MiniMap />
        </ReactFlow>
      </div>
    </div>
  );
}
